import Button from '@/common/components/base/button/Button';
import LoadingButton from '@/common/components/base/button/loadingButton';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { getBanksOption } from '@/common/queries/selections/Banks/banksOption';
import { requiredText } from '@/common/utils/rules/publicRules';
import ImagesFabMainModal from '@/modules/adjusterReportModules/adjusterReport/components/imagesFab/ImagesFabMainModal';
import { getBankAccountQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getBankAccount/getBankAccountQueryOptions';
import { patchBankAccountQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchBankAccount/patchBankAccountQueryOptions';
import { PathBankAccountFormType } from '@/modules/adjusterReportModules/IncidentParticipants/types/BankAccountType';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Flex } from 'styled-system/jsx';

const EditBankAccount = () => {
  const methods = useForm<PathBankAccountFormType>({ mode: 'onChange' });
  const { id, damagedId } = useParams();
  const { mutate } = useMutation(patchBankAccountQueryOptions());
  const navigate = useNavigate();
  const submitHandler = (data: PathBankAccountFormType) => {
    mutate(
      {
        damagedId: +damagedId!,
        id: +id!,
        formData: {
          ...data,
          account_owner_name: data?.account_owner_name ?? null,
          account_number: data?.account_number ?? null,

          account_bank: data?.account_bank?.value ?? null,
          account_card_number: data?.account_card_number ?? null,
          account_sheba_number: data?.account_sheba_number ?? null,
        },
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };
  const { data: BankData } = useQuery(getBanksOption());
  const { data: bankAccountData, isPending } = useQuery(
    getBankAccountQueryOptions({ damaged_id: +damagedId!, id: +id! }),
  );
  useEffect(() => {
    if (bankAccountData) {
      methods.reset({
        account_owner_name: bankAccountData?.account_owner_name,
        account_number: bankAccountData?.account_number,
        account_bank: bankAccountData?.account_bank
          ? {
              label: bankAccountData?.account_bank?.name as string,
              value: bankAccountData?.account_bank?.id as number,
            }
          : null,
        account_card_number: bankAccountData?.account_card_number,
        account_sheba_number: bankAccountData?.account_sheba_number,
      });
    }
  }, [bankAccountData]);
  return (
    <FormProvider {...methods}>
      <Flex
        paddingTop={'24px'}
        px={'16px'}
        direction="column"
        gap={1}
        marginBottom={'40'}
      >
        <ModalInput
          name="account_bank"
          title="بانک ها"
          label="بانک"
          placeholder="انتخاب کنید"
          optionalLabel
          loading={isPending}
          options={BankData?.map((item) => ({
            label: item.name,
            value: item.id,
          }))}
        />
        <TextInput
          name="account_owner_name"
          label="نام دارنده حساب"
          placeholder="نام دارنده حساب را وارد کنید"
          rules={{
            pattern: {
              value: /^[\u0600-\u06FF\s]+$/,
              message: 'لطفا فقط از حروف فارسی استفاده کنید',
            },
            required: { value: true, message: requiredText },
          }}
        />
        <TextInput
          noCharInput
          name="account_card_number"
          label="شماره کارت"
          placeholder="شماره کارت را وارد کنید"
          max={16}
          rules={{
            pattern: {
              value: /^[0-9]{16}$/,
              message: 'شماره کارت باید دقیقاً 16 رقم باشد',
            },
            required: { value: true, message: requiredText },
          }}
        />
        <TextInput
          noCharInput
          name="account_sheba_number"
          label="شماره شبا(بدون IR)"
          placeholder="شماره شبا را وارد کنید"
          max={24}
          rules={{
            pattern: {
              value: /^[0-9]{24}$/,
              message: 'شماره شبا باید دقیقاً 24 رقم باشد',
            },
            required: { value: true, message: requiredText },
          }}
        />{' '}
        <TextInput
          noCharInput
          name="account_number"
          label="شماره حساب"
          placeholder="شماره حساب را وارد کنید"
          max={13}
          rules={{
            required: { value: true, message: requiredText },
            pattern: {
              value: /^[0-9]{13}$/,
              message: 'شماره حساب باید دقیقا 13 رقم و فقط شامل اعداد باشد',
            },
          }}
        />
      </Flex>
      <Button
        onClick={methods?.handleSubmit(submitHandler)}
        type="submit"
        width="100%"
        mb={'24px'}
        disabled={!methods.formState.isValid || methods.formState.isSubmitting}
      >
        ثبت
        {methods.formState.isSubmitting && (
          <LoadingButton loading={methods.formState.isSubmitting} />
        )}
      </Button>
      <ImagesFabMainModal state={'propertyDamaged'} />
    </FormProvider>
  );
};

export default EditBankAccount;
